import React, { useEffect } from 'react';
import {
    Button,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Snackbar,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Add, Delete, Check, Close, Edit } from '@material-ui/icons';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

import { observer } from 'mobx-react-lite';
import licenseFormConfigStore from '../../stores/license/licenseFormConfigStore';
import './formConfig.css';

const LicenseFormConfig: React.FC = observer(() => {
    const store = licenseFormConfigStore;

    useEffect(() => {
        store.fetchDepartments();
    }, [store]);

    const handleDragStart = (e: React.DragEvent<HTMLButtonElement>, dragIndex: number, type: 'question' | 'option') => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ dragIndex, type }));
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number, type: 'question' | 'option') => {
        e.preventDefault();
        const data = e.dataTransfer.getData('text/plain');
        if (!data) return;

        const { dragIndex, type: dragType } = JSON.parse(data);
        if (dragType !== type || dragIndex === dropIndex || isNaN(dragIndex)) return;

        if (type === 'question') {
            store.handleReorderQuestions(dragIndex, dropIndex);
        } else if (type === 'option') {
            store.handleReorderOptions(dragIndex, dropIndex);
        }
    };

    return (
        <div>
            <Typography variant="h4" component="h1" gutterBottom>
                License Review Form Configuration
            </Typography>
            <Typography variant="body1" gutterBottom>
                This page is used to configure the review questions in the license forms.
                You can add, remove, and reorder questions (drag from the icon).
            </Typography>
            <hr />

            <Paper style={{ padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <Typography variant="h6" style={{ marginRight: 16 }}>
                        Department Configuration
                    </Typography>

                    <Tooltip title="Add Department">
                        <IconButton color="primary" onClick={() => store.handleAddDepartment()}>
                            <Add />
                        </IconButton>
                    </Tooltip>
                </div>

                <FormControl variant="outlined" style={{ minWidth: 250, marginBottom: 16 }}>
                    <InputLabel id="department-select-label">Select Department</InputLabel>
                    <Select
                        labelId="department-select-label"
                        value={store.selectedDepartmentId || ''}
                        onChange={(e) => store.setSelectedDepartment(e.target.value as string)}
                        label="Select Department"
                        disabled={store.departments.length === 0}
                    >
                        {store.departments.map(dept => (
                            <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {store.selectedDepartmentId && (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Edit />}
                            onClick={() => store.handleEditDepartment(store.selectedDepartmentId!)}
                        >
                            Rename
                        </Button>

                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<Delete />}
                            onClick={() => {
                                if (window.confirm('Are you sure you want to delete this department configuration?')) {
                                    store.handleRemoveDepartment(store.selectedDepartmentId!);
                                }
                            }}
                        >
                            Delete
                        </Button>
                    </div>
                )}
            </Paper>

            {store.fetchError && (
                <Typography variant="body1" color="error" gutterBottom>
                    {store.fetchError}
                </Typography>
            )}

            <hr />
            {store.selectedDepartmentId ? (
                <Paper style={{ padding: 16 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => store.handleAddQuestion()}
                        disabled={!!store.fetchError}
                    >
                        Add Question
                    </Button>
                    <List>
                        {store.questions.map((question, qIndex) => (
                            <div
                                key={question.sortOrder}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, qIndex, 'question')}
                                style={{ margin: '8px 0' }}
                            >
                                <Paper style={{ padding: 8 }}>
                                    {question.options.length < 2 && (
                                        <Typography variant="caption" color="error" style={{ marginBottom: 8 }}>
                                            This question needs at least 2 options
                                        </Typography>
                                    )}
                                    <ListItem
                                        button
                                        onClick={() => store.handleSelectQuestion(qIndex)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Tooltip title="Drag to reorder">
                                            <IconButton
                                                draggable={true}
                                                onDragStart={(e) => handleDragStart(e, qIndex, 'question')}
                                                style={{ cursor: 'move', marginRight: 8 }}
                                            >
                                                <DragIndicatorIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <ListItemText
                                            primary={question.question}
                                            style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                                        />
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Edit question text">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => store.handleEditQuestion(qIndex)}
                                                    style={{ marginRight: 4, zIndex: 1 }}
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </Tooltip>
                                            {store.confirmDeleteIndex === qIndex ? (
                                                <>
                                                    <Tooltip title="Confirm Delete">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => store.handleRemoveQuestion(qIndex)}
                                                            style={{ marginRight: 4, zIndex: 1 }}
                                                        >
                                                            <Check />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel Delete">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => store.setConfirmDeleteIndex(null)}
                                                            style={{ marginLeft: 4, zIndex: 1 }}
                                                        >
                                                            <Close />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Tooltip title="Delete Question">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => store.setConfirmDeleteIndex(qIndex)}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {store.selectedQuestionIndex === qIndex && (
                                        <div style={{ marginTop: 8 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={question.isMultiSelect}
                                                        onChange={() => store.toggleMultiSelect(qIndex)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Allow multiple selections"
                                            />
                                            <Typography variant="h6" component="h6">
                                                Options for: {question.question}
                                            </Typography>
                                            <Tooltip title="Add Option">
                                                <IconButton color="primary" onClick={() => store.handleAddOption()}>
                                                    <Add />
                                                </IconButton>
                                            </Tooltip>
                                            <List>
                                                {question.options.map((option, oIndex) => (
                                                    <div
                                                        key={oIndex}
                                                        onDragOver={handleDragOver}
                                                        onDrop={(e) => handleDrop(e, oIndex, 'option')}
                                                        style={{ margin: '8px 0' }}
                                                    >
                                                        <ListItem>
                                                            <Tooltip title="Drag to reorder">
                                                                <IconButton
                                                                    draggable={true}
                                                                    onDragStart={(e) => handleDragStart(e, oIndex, 'option')}
                                                                    style={{ cursor: 'move', marginRight: 8 }}
                                                                >
                                                                    <DragIndicatorIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <ListItemText
                                                                primary={option.option}
                                                                style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                                                            />
                                                            <ListItemSecondaryAction>
                                                                <Tooltip title="Edit Option">
                                                                    <IconButton
                                                                        edge="end"
                                                                        onClick={() => store.handleEditOption(qIndex, oIndex)}
                                                                        style={{ marginRight: 8, zIndex: 1 }}
                                                                    >
                                                                        <Edit />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {store.confirmDeleteOptionIndex === oIndex ? (
                                                                    <>
                                                                        <Tooltip title="Confirm Delete">
                                                                            <IconButton
                                                                                edge="end"
                                                                                onClick={() => store.handleRemoveOption(oIndex)}
                                                                                style={{ marginRight: 8, zIndex: 1 }}
                                                                            >
                                                                                <Check />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Cancel Delete">
                                                                            <IconButton
                                                                                edge="end"
                                                                                onClick={() => store.setConfirmDeleteOptionIndex(null)}
                                                                                style={{ marginLeft: 8, zIndex: 1 }}
                                                                            >
                                                                                <Close />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </>
                                                                ) : (
                                                                    <Tooltip title="Delete Option">
                                                                        <IconButton
                                                                            edge="end"
                                                                            onClick={() => store.setConfirmDeleteOptionIndex(oIndex)}
                                                                        >
                                                                            <Delete />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </ListItemSecondaryAction>
                                                        </ListItem>
                                                    </div>
                                                ))}
                                            </List>
                                        </div>
                                    )}
                                </Paper>
                            </div>
                        ))}
                    </List>
                </Paper>
            ) : (
                <Typography variant="body1" color="textSecondary">
                    {store.departments.length > 0
                        ? "Please select a department to manage its configuration"
                        : "Please add a department first"}
                </Typography>
            )}

            {store.selectedDepartmentId && (
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => store.handleSave()}
                    disabled={!store.isSaveEnabled}
                    className="save-button"
                    style={{ marginTop: 16 }}
                >
                    Save
                </Button>
            )}

            <Dialog
                open={store.isQuestionDialogOpen}
                onClose={() => store.handleCancelEdit()}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>{store.editQuestionIndex !== null ? 'Edit Question' : 'Add New Question'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Question"
                        value={store.editQuestionIndex !== null ? store.editedQuestion : store.newQuestion}
                        onChange={(e) => {
                            if (store.editQuestionIndex !== null) {
                                store.editedQuestion = e.target.value;
                            } else {
                                store.newQuestion = e.target.value;
                            }
                        }}
                        fullWidth
                        multiline
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => store.handleCancelEdit()} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (store.editQuestionIndex !== null) {
                                store.handleConfirmEditQuestion();
                            } else {
                                store.handleConfirmAddQuestion();
                            }
                        }}
                        color="primary"
                        disabled={!(store.editQuestionIndex !== null ? store.editedQuestion.trim() : store.newQuestion.trim())}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={store.isOptionDialogOpen}
                onClose={() => store.handleCancelEdit()}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>{store.editOptionIndex !== null ? 'Edit Option' : 'Add New Option'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Option"
                        value={store.editOptionIndex !== null ? store.editedOption : store.newOption}
                        onChange={(e) => {
                            if (store.editOptionIndex !== null) {
                                store.editedOption = e.target.value;
                            } else {
                                store.newOption = e.target.value;
                            }
                        }}
                        fullWidth
                        multiline
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => store.handleCancelEdit()} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (store.editOptionIndex !== null) {
                                store.handleConfirmEditOption();
                            } else {
                                store.handleConfirmAddOption();
                            }
                        }}
                        color="primary"
                        disabled={!(store.editOptionIndex !== null ? store.editedOption.trim() : store.newOption.trim())}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={store.isDepartmentDialogOpen}
                onClose={() => store.handleCancelDepartmentDialog()}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {store.editDepartmentId ? 'Edit Department' : 'Add Department'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Department Name"
                        fullWidth
                        value={store.editDepartmentId ? store.editedDepartmentName : store.newDepartmentName}
                        onChange={(e) => {
                            if (store.editDepartmentId) {
                                store.editedDepartmentName = e.target.value;
                            } else {
                                store.newDepartmentName = e.target.value;
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => store.handleCancelDepartmentDialog()} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (store.editDepartmentId) {
                                store.handleConfirmEditDepartment();
                            } else {
                                store.handleConfirmAddDepartment();
                            }
                        }}
                        color="primary"
                        disabled={!(store.editDepartmentId ? store.editedDepartmentName.trim() : store.newDepartmentName.trim())}
                    >
                        {store.editDepartmentId ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={store.snackbarOpen}
                autoHideDuration={60000}
                onClose={() => store.setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => store.setSnackbarOpen(false)}
                    severity={store.fetchError || store.saveError ? 'error' : 'info'}
                >
                    {store.fetchError || store.saveError || store.saveSuccess}
                </Alert>
            </Snackbar>
        </div>
    );
});

export default LicenseFormConfig;